import React, { Component } from 'react';
import Modal from '../../../components/Modal';
import { Icon } from '../../../components/Icon';
import { urlMap, getAuthHeaders } from '../../../../utils';

/**
 * SummaryPanelContainer
 * 
 * Reusable component that handles:
 * - Fetching chapter summary from API
 * - Displaying summary in modal
 * - Loading and error states
 * 
 * Props:
 * - isOpen: boolean - whether modal is open
 * - onClose: function - callback to close modal
 * - chapter: object - chapter object with id and title
 * - summaryId: string - ID of the summary to fetch
 * - projectId: string - project ID for API call
 */

export default class SummaryPanelContainer extends Component {
  state = {
    summary: null,
    loading: false,
    error: null,
    regenerating: false,
    editedText: '',
    saveStatus: null, // 'saved', 'error', or null
  };

  componentDidMount() {
    if (this.props.isOpen && this.props.summaryId) {
      this.fetchSummary();
    }
  }

  componentDidUpdate(prevProps) {
    // Fetch summary when modal opens and summaryId changes
    if (this.props.isOpen && !prevProps.isOpen && this.props.summaryId) {
      this.fetchSummary();
    }

    // Clear data when modal closes
    if (!this.props.isOpen && prevProps.isOpen) {
      this.setState({ summary: null, error: null, editedText: '' });
    }
  }

  fetchSummary = async () => {
    const { projectId, chapter } = this.props;

    if (!projectId || !chapter?.id) {
      this.setState({ error: 'Missing project or chapter information' });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const url = urlMap.getProjectSummaries(projectId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const summaries = await response.json();
      const chapterSummary = Array.isArray(summaries) 
        ? summaries.find(s => s.chapter_id === chapter.id)
        : null;

      if (!chapterSummary) {
        throw new Error('Summary not found');
      }

      console.log('Loaded summary:', chapterSummary); // Debug log
      
      this.setState({
        summary: chapterSummary,
        loading: false,
        editedText: chapterSummary.summary || chapterSummary.summary_text || '',
      });
    } catch (error) {
      console.error('Error loading summary:', error);
      this.setState({
        error: error.message || 'Failed to load summary',
        loading: false,
      });
    }
  };

  handleRegenerate = async () => {
    const { chapter } = this.props;

    if (!chapter?.id) {
      alert('Cannot regenerate: missing chapter information');
      return;
    }

    this.setState({ regenerating: true, error: null });

    try {
      const url = urlMap.regenerateChapterSummary(chapter.id);
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const updatedSummary = await response.json();
      
      // Update state with new summary
      this.setState({ 
        summary: updatedSummary,
        regenerating: false,
        editedText: updatedSummary.summary || updatedSummary.summary_text || '',
      });

    } catch (error) {
      console.error('Error regenerating summary:', error);
      this.setState({
        error: error.message || 'Failed to regenerate summary',
        regenerating: false,
      });
      alert('Failed to regenerate summary: ' + (error.message || 'Unknown error'));
    }
  };

  handleCancelEdit = () => {
    const { summary } = this.state;
    const originalText = summary?.summary || summary?.summary_text || '';
    this.setState({ 
      editedText: originalText
    });
  };

  handleSaveEdit = async () => {
    const { chapter } = this.props;
    const { editedText, summary } = this.state;

    if (!chapter?.id || !summary?.id) {
      alert('Cannot save: missing required information');
      return;
    }

    this.setState({ regenerating: true, error: null });

    try {
      const url = urlMap.updateChapterSummary(chapter.id, summary.id);
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ summary: editedText }),
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const updatedSummary = await response.json();
      
      // Update state with saved summary
      this.setState({ 
        summary: updatedSummary,
        regenerating: false,
        editedText: updatedSummary.summary || updatedSummary.summary_text || '',
        saveStatus: 'saved',
      });
      
      // Clear save status after 1 second
      setTimeout(() => {
        this.setState({ saveStatus: null });
      }, 1000);
      
    } catch (error) {
      console.error('Error saving summary:', error);
      this.setState({
        error: error.message || 'Failed to save summary',
        regenerating: false,
        saveStatus: 'error',
      });
      
      // Clear error status after 1 second
      setTimeout(() => {
        this.setState({ saveStatus: null });
      }, 1000);
      
      alert('Failed to save summary: ' + (error.message || 'Unknown error'));
    }
  };

  handleTextChange = (e) => {
    this.setState({ editedText: e.target.value });
  };

  render() {
    const { isOpen, onClose, chapter, embedded } = this.props;
    const { summary, loading, error, regenerating, editedText, saveStatus } = this.state;
    
    const content = (
      <>
        {loading ? (
          <div className="summary-modal-loading">
            <div className="summary-modal-loading-spinner"></div>
            <div>Loading summary...</div>
          </div>
        ) : summary || !error ? (
          <div className="summary-modal-content">
            {!embedded && (
              <div className="summary-modal-header">
                <div className="summary-modal-header-row">
                  <h2 className="summary-modal-title">Summary: {chapter?.title}</h2>
                  <button
                    type="button"
                    className={`summary-modal-reload-btn ${regenerating ? 'regenerating' : ''}`}
                    onClick={this.handleRegenerate}
                    disabled={regenerating}
                    title="Regenerate summary"
                    aria-label="Regenerate summary"
                  >
                    {regenerating ? (
                      <span className="summary-modal-regenerating">...</span>
                    ) : (
                      <Icon.Restart />
                    )}
                  </button>
                </div>
              </div>
            )}
            <div className="summary-modal-body">
              {!summary ? (
                <div className="summary-modal-empty">
                  <p>No summary available for this chapter yet.</p>
                  <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-3)' }}>
                    Click the <strong>Regenerate</strong> button above to generate a summary.
                  </p>
                </div>
              ) : (
                <div className="summary-modal-edit-container">
                  <textarea
                    className="summary-modal-edit-textarea"
                    value={editedText}
                    onChange={this.handleTextChange}
                    placeholder="Enter summary text..."
                    disabled={regenerating}
                  />
                  <div className="summary-modal-edit-actions">
                    <button
                      type="button"
                      className="summary-modal-btn summary-modal-btn-secondary"
                      onClick={this.handleCancelEdit}
                      disabled={regenerating}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="summary-modal-btn summary-modal-btn-primary"
                      onClick={this.handleSaveEdit}
                      disabled={regenerating}
                    >
                      {regenerating ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved' : saveStatus === 'error' ? '✗ Error' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="summary-modal-error">
            <p>{error || 'Failed to load summary'}</p>
            <button
              type="button"
              className="btn primary"
              onClick={this.handleRegenerate}
              disabled={regenerating}
              style={{ marginTop: '16px' }}
            >
              {regenerating ? 'Generating...' : 'Generate Summary'}
            </button>
          </div>
        )}
      </>
    );

    if (embedded) {
      return <div className="summary-embedded-content">{content}</div>;
    }

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        {content}
      </Modal>
    );
  }
}
